import type { StateCreator } from 'zustand';
import { referenceDedupKey, type Trip, type TripReference } from '@my-fullstack-app/itinerary-types';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';

/**
 * CRUD for `Trip.references` — the auto-suggested + manually-added
 * reference cards (country info, transit maps, visa, etc.) attached to
 * a trip.
 *
 * `addReferences` (plural) batches new entries from the suggester so
 * we don't fire a sync per card on first hydrate. De-dup uses the
 * shared `referenceDedupKey` from itinerary-types so the server
 * suggester and the client store agree on what counts as a duplicate.
 */

export interface ReferencesSlice {
  /** Append (de-duped) references from the auto-suggester. Updates
   *  the trip's `references` field and marks 'references' dirty. */
  addReferences: (tripId: string, refs: TripReference[]) => void;
  /** Mark a single reference as dismissed (kept on the trip but hidden). */
  dismissReference: (tripId: string, referenceId: string) => void;
  /** Hard-remove a reference. Used for user-added references the user
   *  no longer wants; auto entries should usually be `dismissReference`d
   *  instead so the suggester doesn't re-add them. */
  removeReference: (tripId: string, referenceId: string) => void;
  /** Place-scoped equivalents — operate on `Place.references` rather
   *  than `Trip.references`. They're sync-routed via the `'days'`
   *  TripField since Place is part of the days payload. */
  addPlaceReferences: (tripId: string, placeId: string, refs: TripReference[]) => void;
  dismissPlaceReference: (tripId: string, placeId: string, referenceId: string) => void;
  removePlaceReference: (tripId: string, placeId: string, referenceId: string) => void;
}

function patchPlace(
  trips: Trip[],
  tripId: string,
  placeId: string,
  patch: (refs: TripReference[]) => TripReference[],
): Trip[] {
  return trips.map((t) => {
    if (t.id !== tripId) return t;
    return {
      ...t,
      days: t.days.map((d) => ({
        ...d,
        places: d.places.map((p) =>
          p.id === placeId ? { ...p, references: patch(p.references ?? []) } : p,
        ),
      })),
    };
  });
}

export const createReferencesSlice: StateCreator<ItineraryState, [], [], ReferencesSlice> = (set) => ({
  addReferences: (tripId, refs) => {
    if (refs.length === 0) return;
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        const existing = t.references ?? [];
        const seen = new Set(existing.map(referenceDedupKey));
        const next = [
          ...existing,
          ...refs.filter((r) => !seen.has(referenceDedupKey(r))),
        ];
        return { ...t, references: next };
      }),
    }));
    markTripDirty(tripId, ['references']);
  },

  dismissReference: (tripId, referenceId) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          references: (t.references ?? []).map((r) =>
            r.id === referenceId ? { ...r, dismissed: true } : r,
          ),
        };
      }),
    }));
    markTripDirty(tripId, ['references']);
  },

  removeReference: (tripId, referenceId) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          references: (t.references ?? []).filter((r) => r.id !== referenceId),
        };
      }),
    }));
    markTripDirty(tripId, ['references']);
  },

  addPlaceReferences: (tripId, placeId, refs) => {
    if (refs.length === 0) return;
    set((s) => ({
      trips: patchPlace(s.trips, tripId, placeId, (existing) => {
        const seen = new Set(existing.map(referenceDedupKey));
        return [
          ...existing,
          ...refs.filter((r) => !seen.has(referenceDedupKey(r))),
        ];
      }),
    }));
    // Place lives inside trip.days, so place-reference edits sync via
    // the existing 'days' field — no new TripField needed.
    markTripDirty(tripId, ['days']);
  },

  dismissPlaceReference: (tripId, placeId, referenceId) => {
    set((s) => ({
      trips: patchPlace(s.trips, tripId, placeId, (existing) =>
        existing.map((r) => (r.id === referenceId ? { ...r, dismissed: true } : r)),
      ),
    }));
    markTripDirty(tripId, ['days']);
  },

  removePlaceReference: (tripId, placeId, referenceId) => {
    set((s) => ({
      trips: patchPlace(s.trips, tripId, placeId, (existing) =>
        existing.filter((r) => r.id !== referenceId),
      ),
    }));
    markTripDirty(tripId, ['days']);
  },
});
